import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import './AdminTagManagement.css';
import SvgIcon from '../../components/SvgIcon';

const AdminLearnerTagManagement = ({ onBack, onSwitchView }) => {
  // Main Data States
  const [tags, setTags] = useState([]);
  const [activeTagId, setActiveTagId] = useState(null);
  const [allocatedLearners, setAllocatedLearners] = useState([]);
  const [availableLearners, setAvailableLearners] = useState([]);
  const [loading, setLoading] = useState(true);

  // Project Tag Dropdown & Search State
  const [isTagDropdownOpen, setIsTagDropdownOpen] = useState(false);
  const [tagSearchQuery, setTagSearchQuery] = useState('');
  const tagDropdownRef = useRef(null);
  const tagSearchInputRef = useRef(null);

  // Learner Pool Controls
  const [searchQuery, setSearchQuery] = useState('');
  const [filterTagStatus, setFilterTagStatus] = useState('all'); // 'all' | 'untagged' | 'allocated' | 'other'
  const [sortBy, setSortBy] = useState('name'); // 'name' | 'recent'
  const [selectedLearnerIds, setSelectedLearnerIds] = useState(new Set());
  const [batchAssigning, setBatchAssigning] = useState(false);
  const [isDeletingTag, setIsDeletingTag] = useState(false);

  // Drag and Drop States
  const [draggedLearnerId, setDraggedLearnerId] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);

  // Toast Notification
  const [toast, setToast] = useState(null);

  const showToast = useCallback((msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => {
      setToast(prev => (prev?.msg === msg ? null : prev));
    }, 4000);
  }, []);

  // Fetch Tags List
  const fetchTags = useCallback(async (selectTagId = null) => {
    try {
      const res = await fetch('/api/admin/tags');
      const data = await res.json();
      if (data.status === 'success') {
        setTags(data.data);
        if (selectTagId) {
          setActiveTagId(selectTagId);
        } else if (!activeTagId && data.data.length > 0) {
          setActiveTagId(data.data[0].UserTagID);
        }
      }
    } catch (err) {
      console.error('Failed to fetch tags:', err);
      showToast('Failed to load project tags', 'error');
    }
  }, [activeTagId, showToast]);

  // Fetch Allocated Learners for Active Tag
  const fetchAllocatedLearners = useCallback(async (tagId) => {
    if (!tagId) {
      setAllocatedLearners([]);
      return;
    }
    try {
      const res = await fetch(`/api/admin/tags/${tagId}/learners`);
      const data = await res.json();
      if (data.status === 'success') {
        setAllocatedLearners(data.data);
      }
    } catch (err) {
      console.error('Failed to fetch allocated learners:', err);
    }
  }, []);

  // Fetch Available Learners Pool
  const fetchAvailableLearners = useCallback(async () => {
    try {
      const res = await fetch('/api/admin/learners/tag-pool');
      const data = await res.json();
      if (data.status === 'success') {
        setAvailableLearners(data.data);
      }
    } catch (err) {
      console.error('Failed to fetch learner pool:', err);
    }
  }, []);

  // Initial Load
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([fetchTags(), fetchAvailableLearners()]);
      setLoading(false);
    };
    init();
  }, []);

  // Refresh allocated learners when activeTagId changes
  useEffect(() => {
    if (activeTagId) {
      fetchAllocatedLearners(activeTagId);
      setSelectedLearnerIds(new Set());
    }
  }, [activeTagId, fetchAllocatedLearners]);

  // Click outside to close tag dropdown
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (tagDropdownRef.current && !tagDropdownRef.current.contains(e.target)) {
        setIsTagDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isTagDropdownOpen && tagSearchInputRef.current) {
      tagSearchInputRef.current.focus();
    }
  }, [isTagDropdownOpen]);

  // Active Tag Object
  const activeTag = useMemo(() => {
    return tags.find(t => t.UserTagID === activeTagId) || null;
  }, [tags, activeTagId]);

  // Filtered tags in dropdown search
  const filteredTags = useMemo(() => {
    if (!tagSearchQuery.trim()) return tags;
    const q = tagSearchQuery.toLowerCase();
    return tags.filter(t =>
      t.TagCode.toLowerCase().includes(q) ||
      t.TagName.toLowerCase().includes(q) ||
      (t.Description && t.Description.toLowerCase().includes(q))
    );
  }, [tags, tagSearchQuery]);

  // Set of allocated learner IDs for fast O(1) lookup
  const allocatedLearnerIdsSet = useMemo(() => {
    return new Set(allocatedLearners.map(l => l.LearnerID));
  }, [allocatedLearners]);

  // Filter & Sort Available Learners
  const displayedLearners = useMemo(() => {
    let result = [...availableLearners];

    // 1. Text Search Filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(l =>
        (l.FullName && l.FullName.toLowerCase().includes(q)) ||
        (l.Email && l.Email.toLowerCase().includes(q)) ||
        (l.Username && l.Username.toLowerCase().includes(q)) ||
        (l.OrganisationName && l.OrganisationName.toLowerCase().includes(q)) ||
        (l.Sector && l.Sector.toLowerCase().includes(q)) ||
        (l.Position && l.Position.toLowerCase().includes(q))
      );
    }

    // 2. Tag Status Filter
    if (filterTagStatus === 'untagged') {
      result = result.filter(l => !l.assignedTags || l.assignedTags.length === 0);
    } else if (filterTagStatus === 'allocated') {
      result = result.filter(l => allocatedLearnerIdsSet.has(l.LearnerID));
    } else if (filterTagStatus === 'other') {
      result = result.filter(l => (l.assignedTags || []).some(t => t.UserTagID !== activeTagId));
    }

    // 3. Sorting
    result.sort((a, b) => {
      if (sortBy === 'name') {
        return (a.FullName || '').localeCompare(b.FullName || '');
      }
      return (b.LearnerID || 0) - (a.LearnerID || 0);
    });

    return result;
  }, [availableLearners, searchQuery, filterTagStatus, sortBy, allocatedLearnerIdsSet, activeTagId]);

  // Assign Learners API Handler
  const assignLearnersToTag = async (learnerIdsToAssign) => {
    if (!activeTagId || learnerIdsToAssign.length === 0) return;

    // Quota pre-check if tag has MaxQuota
    if (activeTag && activeTag.MaxQuota !== null && activeTag.MaxQuota !== undefined) {
      const currentEnrolled = allocatedLearners.length;
      const newlyAdding = learnerIdsToAssign.filter(id => !allocatedLearnerIdsSet.has(id)).length;
      if (currentEnrolled + newlyAdding > activeTag.MaxQuota) {
        showToast(
          `Cohort capacity limit reached! Tag "${activeTag.TagCode}" quota is ${activeTag.MaxQuota} learners (Current: ${currentEnrolled}, Adding: ${newlyAdding}).`,
          'error'
        );
        return;
      }
    }

    try {
      const res = await fetch(`/api/admin/tags/${activeTagId}/learners`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ learnerIds: learnerIdsToAssign })
      });
      const data = await res.json();
      if (data.status === 'success') {
        showToast(data.message || `Successfully allocated learner(s) to ${activeTag?.TagCode}!`);
        // Refresh allocated and available lists
        await Promise.all([fetchAllocatedLearners(activeTagId), fetchAvailableLearners(), fetchTags(activeTagId)]);
        setSelectedLearnerIds(new Set());
      } else {
        showToast(data.message || 'Failed to allocate learners', 'error');
      }
    } catch (err) {
      console.error('Error allocating learners:', err);
      showToast('Error connecting to server for learner allocation', 'error');
    }
  };

  // Remove Learner Allocation Handler
  const handleRemoveAllocation = async (learnerId, learnerName = 'Learner') => {
    if (!activeTagId) return;
    try {
      const res = await fetch(`/api/admin/tags/${activeTagId}/learners/${learnerId}`, {
        method: 'DELETE'
      });
      const data = await res.json();
      if (data.status === 'success') {
        showToast(`Removed "${learnerName}" from ${activeTag?.TagCode || 'tag'}.`);
        await Promise.all([fetchAllocatedLearners(activeTagId), fetchAvailableLearners(), fetchTags(activeTagId)]);
      } else {
        showToast(data.message || 'Failed to remove learner allocation', 'error');
      }
    } catch (err) {
      console.error('Error removing learner allocation:', err);
      showToast('Error removing learner from project tag', 'error');
    }
  };

  // Batch Multi-Select Handlers
  const handleToggleSelectLearner = (learnerId) => {
    setSelectedLearnerIds(prev => {
      const next = new Set(prev);
      if (next.has(learnerId)) {
        next.delete(learnerId);
      } else {
        next.add(learnerId);
      }
      return next;
    });
  };

  const handleBatchAssign = async () => {
    if (selectedLearnerIds.size === 0 || !activeTagId) return;
    setBatchAssigning(true);
    await assignLearnersToTag(Array.from(selectedLearnerIds));
    setBatchAssigning(false);
  };

  // Delete Active Project Tag
  const handleDeleteActiveTag = async () => {
    if (!activeTag || isDeletingTag) return;

    const confirmMsg = `Are you sure you want to remove project tag "${activeTag.TagCode}" (${activeTag.TagName})? This will unassign all courses and learners associated with this tag.`;
    if (window.confirm && !window.confirm(confirmMsg)) {
      return;
    }

    setIsDeletingTag(true);
    const tagCodeToRemove = activeTag.TagCode;
    const tagIdToRemove = activeTag.UserTagID;

    try {
      const res = await fetch(`/api/admin/tags/${tagIdToRemove}`, {
        method: 'DELETE'
      });

      if (res.ok) {
        await res.json().catch(() => ({ status: 'success' }));
        showToast(`Project tag "${tagCodeToRemove}" removed successfully.`);

        // Update tags list
        const remainingTags = tags.filter(t => t.UserTagID !== tagIdToRemove);
        setTags(remainingTags);

        // Select first remaining tag or null
        if (remainingTags.length > 0) {
          setActiveTagId(remainingTags[0].UserTagID);
        } else {
          setActiveTagId(null);
          setAllocatedLearners([]);
        }

        // Refresh learner pool
        fetchAvailableLearners();
      } else {
        const data = await res.json().catch(() => ({ message: 'Server error' }));
        throw new Error(data.message || 'Failed to remove project tag');
      }
    } catch (err) {
      console.error('Failed to remove tag:', err);
      showToast(err.message || 'Failed to remove project tag', 'error');
    } finally {
      setIsDeletingTag(false);
    }
  };

  // Drag and Drop Handlers
  const handleDragStart = (e, learnerId) => {
    e.dataTransfer.setData('text/plain', String(learnerId));
    e.dataTransfer.effectAllowed = 'copy';
    setDraggedLearnerId(learnerId);
  };

  const handleDragEnd = () => {
    setDraggedLearnerId(null);
    setIsDragOver(false);
  };

  const handleDropZoneDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    if (!isDragOver) setIsDragOver(true);
  };

  const handleDropZoneDragLeave = (e) => {
    // Only clear hover if leaving drop zone completely
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragOver(false);
    }
  };

  const handleDropZoneDrop = async (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const rawId = e.dataTransfer.getData('text/plain') || draggedLearnerId;
    const learnerId = parseInt(rawId, 10);
    if (!learnerId || !activeTagId) return;

    if (allocatedLearnerIdsSet.has(learnerId)) {
      showToast('Learner is already allocated to this project tag!', 'error');
      return;
    }

    await assignLearnersToTag([learnerId]);
  };

  // Helper for learner initials avatar
  const getInitials = (name) => {
    if (!name) return 'L';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  return (
    <div className="tag-management-container" data-testid="admin-learner-tag-management-page">
      {/* ── Top Bar ── */}
      <header className="tag-top-bar">
        <div className="tag-top-bar-left">
          <div className="tag-breadcrumbs">
            <span
              className="tag-breadcrumb-link"
              onClick={() => onSwitchView ? onSwitchView('adminCourses') : onBack && onBack()}
              data-testid="learner-breadcrumb-courses"
            >
              Courses
            </span>
            <span>/</span>
            <span
              className="tag-breadcrumb-link"
              onClick={onBack}
              data-testid="learner-breadcrumb-hub"
            >
              Project Tag Allocation
            </span>
            <span>/</span>
            <span style={{ color: '#0f172a', fontWeight: 600 }}>Manage Learners</span>
          </div>
          <h1 className="tag-page-title">
            <SvgIcon name="users" size={24} />
            <span>Manage Learners — Project Tag Allocation</span>
          </h1>
          <p className="tag-page-subtitle">
            Inspect active project tag learner rosters, monitor cohort capacity quotas, and drag-and-drop learners from the pool to allocate special-project access.
          </p>
        </div>
        <div className="tag-top-bar-right">
          <button className="btn-secondary" onClick={onBack} data-testid="back-to-hub-btn">
            <SvgIcon name="arrowLeft" size={16} />
            <span>Back to Tag Hub</span>
          </button>
        </div>
      </header>

      {/* ── Toast Notification ── */}
      {toast && (
        <div className={`tag-toast ${toast.type}`} data-testid="learner-tag-toast">
          <SvgIcon name={toast.type === 'success' ? 'checkCircle' : 'alertCircle'} size={18} />
          <span>{toast.msg}</span>
        </div>
      )}

      {/* ── Split-Screen Grid ── */}
      <div className="tag-split-grid">
        {/* ═══════════════════════════════════════════════════════════════════
            LEFT HALF: Active Tag Allocation Inspector & Drop Zone
            ═══════════════════════════════════════════════════════════════════ */}
        <div className="tag-col-left">
          
          {/* Card: Active Tag Selector & Quota Inspector */}
          <div className="tag-card" data-testid="active-tag-card">
            <div className="tag-card-header">
              <h2 className="tag-card-title">
                <SvgIcon name="shield" size={18} />
                <span>Active Tag Allocation Inspector</span>
              </h2>
              <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#4f46e5' }}>
                {tags.length} Active Tags
              </span>
            </div>

            {/* Searchable Project Tag Dropdown */}
            <div className="tag-dropdown-container" ref={tagDropdownRef} data-testid="tag-selector-dropdown">
              <label className="tag-label" htmlFor="learner-tag-dropdown-button" style={{ marginBottom: '0.4rem' }}>
                <span>Select Project Tag:</span>
                <span style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 500 }}>
                  {filteredTags.length} of {tags.length} tags
                </span>
              </label>

              {/* Dropdown Trigger Button */}
              <button
                id="learner-tag-dropdown-button"
                type="button"
                className={`tag-dropdown-trigger ${isTagDropdownOpen ? 'open' : ''}`}
                onClick={() => setIsTagDropdownOpen(prev => !prev)}
                data-testid="tag-dropdown-trigger"
                aria-haspopup="listbox"
                aria-expanded={isTagDropdownOpen}
              >
                <div className="tag-dropdown-trigger-content">
                  {activeTag ? (
                    <div className="tag-dropdown-selected-tag">
                      <span className="tag-code-badge">{activeTag.TagCode}</span>
                      <span className="tag-dropdown-selected-name">{activeTag.TagName}</span>
                      <span className="tag-dropdown-selected-count">
                        <SvgIcon name="users" size={13} />
                        <span>{activeTag.registeredLearnersCount || 0} learners</span>
                      </span>
                    </div>
                  ) : (
                    <span className="tag-dropdown-placeholder">Select a project tag...</span>
                  )}
                </div>
                <span className={`tag-dropdown-arrow ${isTagDropdownOpen ? 'rotated' : ''}`}>
                  <SvgIcon name="chevronDown" size={18} />
                </span>
              </button>

              {/* Dropdown Menu */}
              {isTagDropdownOpen && (
                <div className="tag-dropdown-menu" data-testid="tag-dropdown-menu" role="listbox">
                  {/* Search Input Bar */}
                  <div className="tag-dropdown-search-box">
                    <span className="tag-dropdown-search-icon">
                      <SvgIcon name="search" size={15} />
                    </span>
                    <input
                      ref={tagSearchInputRef}
                      type="text"
                      className="tag-dropdown-search-input"
                      placeholder="Search tags by code, name, or description..."
                      value={tagSearchQuery}
                      onChange={(e) => setTagSearchQuery(e.target.value)}
                      data-testid="tag-dropdown-search-input"
                      onClick={(e) => e.stopPropagation()}
                    />
                    {tagSearchQuery && (
                      <button
                        type="button"
                        className="tag-dropdown-search-clear"
                        onClick={(e) => {
                          e.stopPropagation();
                          setTagSearchQuery('');
                          tagSearchInputRef.current?.focus();
                        }}
                      >
                        ✕
                      </button>
                    )}
                  </div>

                  {/* Dropdown Options List */}
                  <div className="tag-dropdown-list">
                    {filteredTags.length === 0 ? (
                      <div className="tag-dropdown-empty">
                        <span>No tags matching "{tagSearchQuery}"</span>
                      </div>
                    ) : (
                      filteredTags.map(tag => {
                        const isSelected = tag.UserTagID === activeTagId;
                        return (
                          <div
                            key={tag.UserTagID}
                            className={`tag-dropdown-item learner-tag-item ${isSelected ? 'selected' : ''}`}
                            onClick={() => {
                              setActiveTagId(tag.UserTagID);
                              setIsTagDropdownOpen(false);
                              setTagSearchQuery('');
                            }}
                            role="option"
                            aria-selected={isSelected}
                            data-testid={`tag-dropdown-item-${tag.UserTagID}`}
                          >
                            <div className="tag-dropdown-item-header">
                              <div className="tag-dropdown-item-title-group">
                                <span className="tag-code-badge">{tag.TagCode}</span>
                                <strong className="tag-dropdown-item-name" title={tag.TagName}>{tag.TagName}</strong>
                                {tag.isExpired && (
                                  <span className="tag-meta-badge expired">Expired</span>
                                )}
                              </div>
                              {isSelected && (
                                <span className="tag-dropdown-item-check" title="Currently selected">
                                  <SvgIcon name="check" size={16} />
                                </span>
                              )}
                            </div>
                            <div className="tag-dropdown-item-meta">
                              <span className="tag-meta-badge learners" title="Registered Learners">
                                <SvgIcon name="users" size={12} />
                                <span>{tag.registeredLearnersCount || 0} registered learners</span>
                              </span>
                              <span className="tag-meta-badge quota" title="Cohort Quota">
                                <SvgIcon name="barChart" size={12} />
                                <span>{tag.MaxQuota ? `Quota: ${tag.MaxQuota}` : 'Unlimited quota'}</span>
                              </span>
                              {tag.ExpiryDate && (
                                <span className={`tag-meta-badge expiry ${tag.isExpired ? 'expired' : ''}`} title="Tag Expiry Date">
                                  <SvgIcon name="calendar" size={12} />
                                  <span>{tag.isExpired ? `Expired: ${tag.ExpiryDate}` : `Expires: ${tag.ExpiryDate}`}</span>
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Active Tag Details & Cohort Quota Tracker */}
            {activeTag && (
              <div className="active-tag-info-box" data-testid="active-tag-details">
                <div className="active-tag-row">
                  <div className="active-tag-badge-group">
                    <span className="tag-code-pill large">{activeTag.TagCode}</span>
                    <span className="active-tag-name">{activeTag.TagName}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <span className={`tag-status-pill ${activeTag.IsActive && !activeTag.isExpired ? 'pill-active' : 'pill-inactive'}`}>
                      {activeTag.isExpired ? '⚠ Expired' : (activeTag.IsActive ? '● Active Cohort' : '○ Inactive')}
                    </span>
                    <button
                      type="button"
                      className="tag-remove-btn"
                      onClick={handleDeleteActiveTag}
                      disabled={isDeletingTag}
                      title={`Remove project tag "${activeTag.TagCode}"`}
                      data-testid="remove-active-tag-btn"
                    >
                      <SvgIcon name="trash" size={13} />
                      <span>{isDeletingTag ? 'Removing...' : 'Remove Tag'}</span>
                    </button>
                  </div>
                </div>

                {/* Cohort Quota Progress Indicator */}
                <div className="learner-quota-card" data-testid="cohort-quota-indicator">
                  <div className="learner-quota-header">
                    <span className="learner-quota-label">
                      <SvgIcon name="users" size={14} />
                      <span>Cohort Quota Status</span>
                    </span>
                    <span className="learner-quota-stats">
                      {activeTag.MaxQuota ? (
                        <strong>{allocatedLearners.length} / {activeTag.MaxQuota} Enrolled</strong>
                      ) : (
                        <strong>{allocatedLearners.length} Enrolled (Unlimited)</strong>
                      )}
                    </span>
                  </div>

                  {activeTag.MaxQuota && (
                    <div className="learner-quota-bar-wrapper">
                      <div
                        className={`learner-quota-bar-fill ${
                          allocatedLearners.length >= activeTag.MaxQuota
                            ? 'full'
                            : (allocatedLearners.length / activeTag.MaxQuota) >= 0.8
                            ? 'warning'
                            : 'normal'
                        }`}
                        style={{
                          width: `${Math.min(100, Math.round((allocatedLearners.length / activeTag.MaxQuota) * 100))}%`
                        }}
                      />
                    </div>
                  )}

                  <div className="learner-quota-footer">
                    {activeTag.MaxQuota ? (
                      allocatedLearners.length >= activeTag.MaxQuota ? (
                        <span className="quota-status-text full">⚠️ Cohort is at full capacity ({activeTag.MaxQuota} learners)</span>
                      ) : (
                        <span className="quota-status-text">
                          {activeTag.MaxQuota - allocatedLearners.length} seats available
                        </span>
                      )
                    ) : (
                      <span className="quota-status-text">No maximum learner quota limit configured for this tag.</span>
                    )}
                    {activeTag.ExpiryDate && (
                      <span className="quota-expiry-text" style={{ color: activeTag.isExpired ? '#ef4444' : undefined, fontWeight: activeTag.isExpired ? 700 : undefined }}>
                        Expires: {activeTag.ExpiryDate} {activeTag.isExpired && '(Expired)'}
                      </span>
                    )}
                  </div>
                </div>

                {activeTag.Description && (
                  <p className="active-tag-desc">{activeTag.Description}</p>
                )}
              </div>
            )}

            {/* Visual Drop Zone for Learner Allocation */}
            <div
              className={`drop-zone ${isDragOver ? 'drag-hover' : ''}`}
              onDragOver={handleDropZoneDragOver}
              onDragLeave={handleDropZoneDragLeave}
              onDrop={handleDropZoneDrop}
              data-testid="allocated-learners-drop-zone"
            >
              <div className="drop-zone-header">
                <span className="drop-zone-title">
                  <SvgIcon name="download" size={16} />
                  <span>Allocated Learners Drop Zone</span>
                </span>
                <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#4f46e5', backgroundColor: '#e0e7ff', padding: '0.15rem 0.6rem', borderRadius: '10px' }}>
                  {allocatedLearners.length} Allocated
                </span>
              </div>

              {allocatedLearners.length === 0 ? (
                <div className="drop-zone-empty">
                  <div className="drop-zone-empty-icon">
                    <SvgIcon name="download" size={20} />
                  </div>
                  <span className="drop-zone-empty-text">Drag learner cards from the right panel here</span>
                  <span className="drop-zone-empty-sub">
                    or click "+ Assign" on any learner card to link them to {activeTag?.TagCode || 'this tag'}
                  </span>
                </div>
              ) : (
                <div className="allocated-learners-grid" data-testid="allocated-learners-container">
                  {allocatedLearners.map(learner => (
                    <div
                      key={learner.LearnerID}
                      className="allocated-learner-chip"
                      data-testid={`allocated-learner-chip-${learner.LearnerID}`}
                    >
                      <div className="allocated-learner-avatar">
                        {getInitials(learner.FullName)}
                      </div>
                      <div className="allocated-learner-details">
                        <span className="allocated-learner-name">{learner.FullName}</span>
                        <span className="allocated-learner-meta">
                          {learner.Email}
                          {learner.OrganisationName && ` • ${learner.OrganisationName}`}
                        </span>
                      </div>
                      <button
                        className="chip-remove-btn"
                        title="Remove learner from tag"
                        onClick={() => handleRemoveAllocation(learner.LearnerID, learner.FullName)}
                        data-testid={`remove-learner-chip-${learner.LearnerID}`}
                      >
                        <SvgIcon name="x" size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

        </div>

        {/* ═══════════════════════════════════════════════════════════════════
            RIGHT HALF: Learner Pool & Discovery
            ═══════════════════════════════════════════════════════════════════ */}
        <div className="tag-card" data-testid="learner-pool-card">
          <div className="tag-card-header">
            <h2 className="tag-card-title">
              <SvgIcon name="users" size={18} />
              <span>Available Learner Pool</span>
            </h2>
            <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#64748b' }}>
              Showing {displayedLearners.length} learners
            </span>
          </div>

          {/* Search, Filter, Sort, and Batch Controls */}
          <div className="course-pool-controls">
            <div className="course-search-wrapper">
              <span className="course-search-icon">
                <SvgIcon name="search" size={16} />
              </span>
              <input
                type="text"
                className="course-search-input"
                placeholder="Search learners by name, email, organisation, or sector..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="learner-pool-search-input"
              />
            </div>

            <div className="course-filter-row">
              <div className="course-filter-group">
                <label style={{ fontWeight: 600, fontSize: '0.8rem' }}>Filter:</label>
                <select
                  className="tag-select"
                  style={{ padding: '0.4rem 0.6rem', fontSize: '0.8rem' }}
                  value={filterTagStatus}
                  onChange={(e) => setFilterTagStatus(e.target.value)}
                  data-testid="learner-pool-filter-select"
                >
                  <option value="all">All Learners</option>
                  <option value="untagged">Untagged (General)</option>
                  <option value="allocated">Allocated to Active Tag</option>
                  <option value="other">Assigned to Other Tags</option>
                </select>
              </div>

              <div className="course-filter-group">
                <label style={{ fontWeight: 600, fontSize: '0.8rem' }}>Sort:</label>
                <select
                  className="tag-select"
                  style={{ padding: '0.4rem 0.6rem', fontSize: '0.8rem' }}
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  data-testid="learner-pool-sort-select"
                >
                  <option value="name">Name (A - Z)</option>
                  <option value="recent">Most Recent (LearnerID)</option>
                </select>
              </div>

              {/* Batch Assign Selected Button */}
              <button
                className="course-batch-btn"
                onClick={handleBatchAssign}
                disabled={selectedLearnerIds.size === 0 || batchAssigning || !activeTagId}
                data-testid="batch-assign-learners-btn"
              >
                <SvgIcon name="checkSquare" size={14} />
                <span>
                  {batchAssigning
                    ? 'Assigning...'
                    : `Assign Selected (${selectedLearnerIds.size}) to ${activeTag ? activeTag.TagCode : 'Tag'}`}
                </span>
              </button>
            </div>
          </div>

          {/* Learner Cards List (Draggable Cards) */}
          <div className="learner-cards-list" data-testid="learner-cards-list">
            {displayedLearners.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2.5rem', color: '#94a3b8' }}>
                No learners match your search criteria.
              </div>
            ) : (
              displayedLearners.map(learner => {
                const isAllocatedToActive = allocatedLearnerIdsSet.has(learner.LearnerID);
                const isDraggingThis = draggedLearnerId === learner.LearnerID;
                const isChecked = selectedLearnerIds.has(learner.LearnerID);
                const isAtQuota = activeTag?.MaxQuota && allocatedLearners.length >= activeTag.MaxQuota && !isAllocatedToActive;

                return (
                  <div
                    key={learner.LearnerID}
                    className={`learner-pool-card ${isAllocatedToActive ? 'allocated-to-active' : ''} ${isDraggingThis ? 'dragging' : ''}`}
                    draggable={!isAllocatedToActive && !isAtQuota}
                    onDragStart={(e) => handleDragStart(e, learner.LearnerID)}
                    onDragEnd={handleDragEnd}
                    data-testid={`draggable-learner-card-${learner.LearnerID}`}
                  >
                    {/* Checkbox for batch multi-select */}
                    <input
                      type="checkbox"
                      className="course-card-checkbox"
                      checked={isChecked}
                      disabled={isAllocatedToActive || isAtQuota}
                      onChange={() => handleToggleSelectLearner(learner.LearnerID)}
                      data-testid={`learner-checkbox-${learner.LearnerID}`}
                    />

                    {/* Learner Avatar */}
                    <div className="learner-avatar-bubble">
                      {getInitials(learner.FullName)}
                    </div>

                    {/* Card Content */}
                    <div className="course-card-body">
                      <div className="course-card-top">
                        <span className="learner-card-name" title={learner.FullName}>
                          {learner.FullName}
                        </span>
                        <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
                          ID #{learner.LearnerID}
                        </span>
                      </div>

                      <div className="learner-card-sub">
                        <span>{learner.Email}</span>
                        {learner.Username && (
                          <span style={{ color: '#6366f1' }}> • @{learner.Username}</span>
                        )}
                      </div>

                      <div className="learner-org-row">
                        {learner.OrganisationName ? (
                          <span className="learner-org-badge">
                            <SvgIcon name="building" size={12} style={{ flexShrink: 0, marginTop: '2px' }} />
                            <span className="learner-org-text">
                              <span>{learner.OrganisationName}</span>
                              {learner.Sector && (
                                <span className="learner-sector-text" style={{ opacity: 0.75, marginLeft: '0.35rem', fontWeight: 500 }}>
                                  ({learner.Sector})
                                </span>
                              )}
                            </span>
                          </span>
                        ) : (
                          <span className="learner-org-badge" style={{ color: '#64748b' }}>
                            Individual Learner {learner.Sector ? `(${learner.Sector})` : ''}
                          </span>
                        )}
                        {learner.Position && (
                          <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
                            • {learner.Position}
                          </span>
                        )}
                      </div>

                      {/* Tag Status Badges */}
                      <div className="course-card-tags">
                        {(!learner.assignedTags || learner.assignedTags.length === 0) ? (
                          <span className="tag-status-pill pill-untagged">Untagged (General)</span>
                        ) : (
                          learner.assignedTags.map((t, idx) => {
                            const isThisActiveTag = t.UserTagID === activeTagId;
                            return (
                              <span
                                key={t.UserTagID || t.TagCode || idx}
                                className={`tag-status-pill ${isThisActiveTag ? 'pill-active-tag' : 'pill-other-tag'}`}
                              >
                                {isThisActiveTag ? `✓ ${t.TagCode}` : t.TagCode}
                              </span>
                            );
                          })
                        )}
                      </div>
                    </div>

                    {/* Card Action Button */}
                    <div className="course-card-actions">
                      {isAllocatedToActive ? (
                        <button
                          className="card-action-btn assigned"
                          disabled
                          title="Already allocated to this tag"
                          data-testid={`allocated-badge-btn-${learner.LearnerID}`}
                        >
                          <SvgIcon name="check" size={14} />
                          <span>Enrolled</span>
                        </button>
                      ) : (
                        <button
                          className="card-action-btn assign"
                          onClick={() => assignLearnersToTag([learner.LearnerID])}
                          disabled={isAtQuota}
                          title={isAtQuota ? 'Cohort quota limit reached' : `Assign ${learner.FullName} to ${activeTag?.TagCode || 'tag'}`}
                          data-testid={`assign-learner-btn-${learner.LearnerID}`}
                        >
                          <span>+ Assign</span>
                        </button>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

export default AdminLearnerTagManagement;
